import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { Box, Chip, Link as MUILink, Paper, Stack, Typography } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import PermissionedComponent from '~/components/Common/PermissionedComponent';
import UserEmailChangeTable from '~/components/System/UserEmailChangeTable';
import UserLoginTable from '~/components/System/UserLoginTable';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER } from '~/consts/roles';

import { formatDateOrdinalWithClock } from '~/services/TimeService';

import type { UserTenancy } from '../UserDetails';

import IdentitySignInHistory from './IdentitySignInHistory';

interface Props {
  user: App.User;
  tenancy: Array<UserTenancy>;
}

export default function UserAccountHistory({ user, tenancy }: Props) {
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h2" component="h2">
          Account & login history
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Login activity, contact changes and account origins.
        </Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} gap={3}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Account created
            </Typography>
            <Typography>{formatDateOrdinalWithClock(user.created_date)}</Typography>
          </Box>
          {user.signup_domain && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Signup domain
              </Typography>
              <Typography>{user.signup_domain}</Typography>
            </Box>
          )}
          {!!tenancy?.length && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Other domains with the same email
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {tenancy.map((item) => (
                  <Chip key={item.tenant} label={item.tenant} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
      <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
        <Paper component="section" variant="outlined" sx={{ p: 3 }}>
          <PageSubheader title="Recent logins" />
          <UserLoginTable userId={user.memberId} />
        </Paper>
      </PermissionedComponent>
      <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
        <IdentitySignInHistory accountId={user.id_member} />
      </PermissionedComponent>
      <Paper component="section" variant="outlined" sx={{ p: 3 }}>
        <PageSubheader title="Email changes" />
        <UserEmailChangeTable userId={user.memberId} />
      </Paper>
      <Paper component="section" variant="outlined" sx={{ p: 3 }}>
        <PageSubheader title="Phone number changes" />
        <MUILink component={Link} to={`/users/${user.id_member}/history?filter=user_changes`}>
          View contact change timeline
        </MUILink>
      </Paper>
    </Stack>
  );
}
